import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminGetTenants } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmt$(n) { return n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(raw) { if (!raw) return '—'; return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }

function StatusBadge({ status }) {
  const cfg = {
    active:    { color: '#059669', bg: 'rgba(5,150,105,0.08)' },
    inactive:  { color: '#dc2626', bg: 'rgba(239,68,68,0.08)' },
    suspended: { color: '#b45309', bg: 'rgba(245,158,11,0.08)' },
  };
  const c = cfg[status] || cfg.inactive;
  return <span style={{ background: c.bg, color: c.color, borderRadius: 5, fontSize: 10, fontWeight: 600, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{status || 'unknown'}</span>;
}

export default function AdminTenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [sortCol, setSortCol] = useState('created_on_utc');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  useEffect(() => {
    adminGetTenants()
      .then(res => { setTenants(res.tenants || res || []); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, []);

  const filtered = tenants
    .filter(t => !search || t.company_name?.toLowerCase().includes(search.toLowerCase()) || t.email?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
      if (typeof av === 'number' || typeof bv === 'number') { av = Number(av||0); bv = Number(bv||0); }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div style={s.page}>
      <AdminSidebar active="tenants" />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Tenants</h1>
            <p style={s.sub}>{tenants.length} total accounts</p>
          </div>
          <input style={s.search} placeholder="Search by company or email…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>
                  {[
                    { label: 'Company',     col: 'company_name'    },
                    { label: 'Plan',        col: 'plan'            },
                    { label: 'Status',      col: 'status'          },
                    { label: 'Users',       col: 'user_count'      },
                    { label: 'Jobs',        col: 'job_count'       },
                    { label: 'Total Spent', col: 'total_spent'     },
                    { label: 'Created',     col: 'created_on_utc'  },
                  ].map(({ label, col }) => (
                    <th key={col} style={{ ...s.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => toggleSort(col)}>
                      {label} {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : <span style={{ color: '#d1d5db' }}>↕</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.tenant_id} style={{ ...s.tr, cursor: 'pointer' }} onClick={() => navigate(`/admin/tenants/${t.tenant_id}`)}>
                    <td style={{ ...s.td, fontWeight: 600, color: NAVY }}>
                      {t.company_name || '—'}
                      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 400, marginTop: 1 }}>{t.email}</div>
                    </td>
                    <td style={{ ...s.td, color: '#64748b', textTransform: 'capitalize' }}>{t.plan || 'free'}</td>
                    <td style={s.td}><StatusBadge status={t.status} /></td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{t.user_count ?? 0}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{t.job_count ?? 0}</td>
                    <td style={{ ...s.td, color: t.total_spent ? '#059669' : '#94a3b8', fontWeight: t.total_spent ? 600 : 400 }}>{fmt$(t.total_spent)}</td>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(t.created_on_utc)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>No tenants found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page:   { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:   { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
  title:  { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:    { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  search: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, fontSize: 13, padding: '9px 14px', width: 260, outline: 'none' },
  card:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr:     { borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' },
  td:     { color: '#374151', fontSize: 13, padding: '12px 16px', verticalAlign: 'middle' },
};
